import { useSyncExternalStore } from 'react';

/**
 * 「显示非当日达订单」这个勾选框的状态 —— **列表和地图共用一份**。
 *
 * ★ 用户原话:「注意在列表中勾选后切换到地图模式也有效」。
 *
 *   放在 Today.tsx 自己的 useState 里是不行的:切到地图那一屏,
 *   那个组件卸载了,状态跟着没;切回来又变回不勾。
 *   而更糟的是**两屏不一致** —— 列表上藏起来、地图上又画出来,
 *   他会照着地图规划一条把明天的活也算进去的路线。
 *
 * ★ 记在 localStorage 里,刷新/重开之后还是上次那个选择。
 *
 *   我一开始只放在模块变量里,理由是「这是『我现在想看一眼明天的』,
 *   不是长期偏好」。用户要的是记住 —— 而且他是对的:
 *   排明天的活是有些店每天都要干的事,每次打开都要重新勾一下,
 *   那个勾选框就成了每天的一道手续。
 *
 *   和「显示发货信息」那个开关同一套做法(见 PickupToggle)。
 *
 * ★ localStorage 读写都包 try —— 隐私模式下会抛,
 *   而一个勾选框不该把整个司机端搞白屏。
 *
 * ★ 用 useSyncExternalStore 而不是 Context:这个状态跨的是两条
 *   懒加载的路由,套一层 Provider 要动 App.tsx 的结构,
 *   而它本质上就是一个布尔值。
 */
const KEY = 'delivery.driver.showFuture';

function readStored(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    // 隐私模式下读不到 —— 用默认值,不该因此出错
    return false;
  }
}

let showFuture = readStored();
const listeners = new Set<() => void>();

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setShowFuture(v: boolean): void {
  if (showFuture === v) return;
  showFuture = v;
  try {
    localStorage.setItem(KEY, v ? '1' : '0');
  } catch {
    /* 存不下就算了,只是下次打开要重新勾一下 */
  }
  listeners.forEach((l) => l());
}

export function useShowFuture(): [boolean, (v: boolean) => void] {
  const v = useSyncExternalStore(
    subscribe,
    () => showFuture,
    () => false, // 服务端渲染时当成不勾 —— 这个 App 不做 SSR,给个稳定值防止告警
  );
  return [v, setShowFuture];
}
